import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { getBackendURL } from '../utils/backendURL';

// Import the admin components
import AdminLogin from './ops/AdminLogin';
import AdminDashboard from './ops/AdminDashboard';

// Admin Context
const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

// Admin Provider
const AdminProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  // Use main app backend URL instead of separate admin backend
  const BACKEND_URL = getBackendURL();

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Admin-Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Admin-Authorization'];
    }
  }, [token]);

  // Verify admin session on app load
  useEffect(() => {
    const verifySession = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Try to fetch dashboard overview to verify token
        const response = await axios.get(`${BACKEND_URL}/api/ops/dashboard/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data) {
          setAdminUser({ 
            email: 'admin@lexa.run', 
            role: 'super_admin',
            id: 'admin'
          });
        }
      } catch (error) {
        console.error('Admin session verification failed:', error);
        localStorage.removeItem('admin_token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, [token, BACKEND_URL]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ops/auth/login`, {
        email,
        password
      });

      const { access_token, admin_user } = response.data;
      setToken(access_token);
      setAdminUser(admin_user);
      localStorage.setItem('admin_token', access_token);
      
      return { success: true };
    } catch (error) {
      console.error('Admin login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setAdminUser(null);
    localStorage.removeItem('admin_token');
  };

  const value = {
    adminUser,
    login,
    logout,
    loading,
    token,
    BACKEND_URL
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

// Main OPS Dashboard Component
const OPSDashboard = ({ onBack, user }) => {
  // Note: OPS Dashboard has its own admin authentication
  // No security check needed here as AdminProvider handles admin authentication

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminProvider>
        <OPSContent onBack={onBack} />
      </AdminProvider>
    </div>
  );
};

// OPS Content Component
const OPSContent = ({ onBack }) => {
  const { adminUser, loading } = useAdmin();
  const [currentView, setCurrentView] = useState('login');

  useEffect(() => {
    if (adminUser) {
      setCurrentView('dashboard');
    } else {
      setCurrentView('login');
    }
  }, [adminUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (currentView === 'login') {
    return <AdminLogin onBack={onBack} />;
  }

  return <AdminDashboard onBack={onBack} />;
};

export default OPSDashboard;